import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { ApiError, request } from "./api";

type SessionUser = { id: string; email: string };
type LoginResponse = {
  access_token: string;
  refresh_token?: string;
  expires_at?: number;
  user_id: string;
  email: string;
};

type AuthContextValue = {
  loading: boolean;
  accessToken: string | null;
  refreshToken: string | null;
  user: SessionUser | null;
  login: (email: string, password: string) => Promise<void>;
  signup: (fullName: string, email: string, password: string) => Promise<string>;
  googleLogin: () => Promise<void>;
  adoptSession: (session: LoginResponse) => void;
  logout: () => Promise<void>;
  authRequest: <T>(endpoint: string, options?: RequestInit) => Promise<T>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function readStoredUser(): SessionUser | null {
  const id = localStorage.getItem("limitx_user_id");
  const email = localStorage.getItem("limitx_user_email");
  return id && email ? { id, email } : null;
}

function storeSession(session: LoginResponse) {
  localStorage.setItem("limitx_access_token", session.access_token);
  if (session.refresh_token) localStorage.setItem("limitx_refresh_token", session.refresh_token);
  localStorage.setItem("limitx_user_id", session.user_id);
  localStorage.setItem("limitx_user_email", session.email);
  if (session.expires_at) localStorage.setItem("limitx_expires_at", String(session.expires_at));
}

function clearSession() {
  [
    "limitx_access_token",
    "limitx_refresh_token",
    "limitx_user_id",
    "limitx_user_email",
    "limitx_expires_at"
  ].forEach((key) => localStorage.removeItem(key));
}

function jwtExpiresSoon(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split(".")[1] || ""));
    return !payload.exp || payload.exp * 1000 < Date.now() + 5 * 60_000;
  } catch {
    return true;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [accessToken, setAccessToken] = useState<string | null>(() =>
    localStorage.getItem("limitx_access_token")
  );
  const [refreshToken, setRefreshToken] = useState<string | null>(() =>
    localStorage.getItem("limitx_refresh_token")
  );
  const [user, setUser] = useState<SessionUser | null>(() => readStoredUser());
  const refreshPromise = useRef<Promise<string | null> | null>(null);

  const adoptSession = useCallback((session: LoginResponse) => {
    storeSession(session);
    setAccessToken(session.access_token);
    setRefreshToken(session.refresh_token || localStorage.getItem("limitx_refresh_token"));
    setUser({ id: session.user_id, email: session.email });
  }, []);

  const refresh = useCallback(async () => {
    if (!refreshToken) return null;
    if (!refreshPromise.current) {
      refreshPromise.current = request<LoginResponse>("/api/auth/refresh", {
        method: "POST",
        body: JSON.stringify({ refresh_token: refreshToken })
      })
        .then((session) => {
          adoptSession(session);
          return session.access_token;
        })
        .catch(() => {
          clearSession();
          setAccessToken(null);
          setRefreshToken(null);
          setUser(null);
          return null;
        })
        .finally(() => {
          refreshPromise.current = null;
        });
    }
    return refreshPromise.current;
  }, [adoptSession, refreshToken]);

  const authRequest = useCallback(
    async <T,>(endpoint: string, options: RequestInit = {}) => {
      let token = accessToken;
      if (token && jwtExpiresSoon(token) && refreshToken) token = await refresh();
      if (!token) throw new ApiError("Authentication required", 401);
      try {
        return await request<T>(endpoint, options, token);
      } catch (error) {
        if (error instanceof ApiError && error.status === 401 && refreshToken) {
          const freshToken = await refresh();
          if (freshToken) return request<T>(endpoint, options, freshToken);
        }
        throw error;
      }
    },
    [accessToken, refresh, refreshToken]
  );

  useEffect(() => {
    let cancelled = false;
    async function bootstrap() {
      if (!accessToken) {
        if (refreshToken) await refresh();
        if (!cancelled) setLoading(false);
        return;
      }
      try {
        const profile = await authRequest<{ id: string; email: string }>("/api/user/profile");
        if (!cancelled) {
          setUser({ id: profile.id, email: profile.email });
          localStorage.setItem("limitx_user_id", profile.id);
          localStorage.setItem("limitx_user_email", profile.email);
        }
      } catch {
        if (!cancelled) {
          clearSession();
          setAccessToken(null);
          setRefreshToken(null);
          setUser(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (accessToken && refreshToken && jwtExpiresSoon(accessToken)) void refresh();
    }, 60_000);
    return () => window.clearInterval(timer);
  }, [accessToken, refresh, refreshToken]);

  const login = useCallback(
    async (email: string, password: string) => {
      const session = await request<LoginResponse>("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password })
      });
      adoptSession(session);
    },
    [adoptSession]
  );

  const signup = useCallback(
    async (fullName: string, email: string, password: string) => {
      const result = await request<
        LoginResponse & { success: boolean; message: string; access_token?: string | null }
      >("/api/auth/signup", {
        method: "POST",
        body: JSON.stringify({ full_name: fullName, email, password })
      });
      if (result.access_token) adoptSession(result as LoginResponse);
      return result.message;
    },
    [adoptSession]
  );

  const googleLogin = useCallback(async () => {
    const result = await request<{ url: string }>("/api/auth/google", { method: "POST" });
    window.location.assign(result.url);
  }, []);

  const logout = useCallback(async () => {
    if (accessToken) {
      await request("/api/auth/logout", { method: "POST" }, accessToken).catch(() => null);
    }
    clearSession();
    setAccessToken(null);
    setRefreshToken(null);
    setUser(null);
  }, [accessToken]);

  const value = useMemo(
    () => ({
      loading,
      accessToken,
      refreshToken,
      user,
      login,
      signup,
      googleLogin,
      adoptSession,
      logout,
      authRequest
    }),
    [
      accessToken,
      adoptSession,
      authRequest,
      googleLogin,
      loading,
      login,
      logout,
      refreshToken,
      signup,
      user
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}
